import React from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  PerformanceDashboardResponse,
  PerformanceMatrixRow,
} from "./performanceApi";

interface Props {
  data: PerformanceDashboardResponse | null;
  editing: boolean;
  onChangeMarks: (params: {
    subjectId: string;
    examId: string;
    marksObtained: number;
    maxMarks: number;
  }) => void;
}

const PerformanceTable: React.FC<Props> = ({
  data,
  editing,
  onChangeMarks,
}) => {
  if (!data || !data.exams.length || !data.performanceMatrix.length) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 2, borderRadius: 2, textAlign: "center" }}
      >
        <Typography color="textSecondary">
          No performance data available for selected filters.
        </Typography>
      </Paper>
    );
  }

  const exams = data.exams;

  const handleChangeCell = (
    row: PerformanceMatrixRow,
    examId: string,
    field: "marksObtained" | "maxMarks",
    value: string,
  ) => {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return;
    }
    const existing = row.examResults.find((e) => e.examId === examId);
    const marksObtained =
      field === "marksObtained"
        ? numeric
        : existing
        ? existing.marksObtained
        : 0;
    const maxMarks =
      field === "maxMarks"
        ? numeric
        : existing
        ? existing.maxMarks
        : 0;

    onChangeMarks({
      subjectId: row.subjectId,
      examId,
      marksObtained,
      maxMarks,
    });
  };

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Subject</TableCell>
            {exams.map((exam) => (
              <TableCell key={exam.id} align="center">
                {exam.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.performanceMatrix.map((row) => (
            <TableRow key={row.subjectId}>
              <TableCell>{row.subjectName}</TableCell>
              {exams.map((exam) => {
                const cell = row.examResults.find(
                  (e) => e.examId === exam.id,
                );
                if (!editing) {
                  return (
                    <TableCell key={exam.id} align="center">
                      {cell
                        ? `${cell.marksObtained}/${cell.maxMarks} ${
                            cell.grade ? `(${cell.grade})` : ""
                          }`
                        : "-"}
                    </TableCell>
                  );
                }
                return (
                  <TableCell key={exam.id} align="center">
                    <TextField
                      variant="outlined"
                      size="small"
                      type="number"
                      placeholder="Marks"
                      value={cell ? cell.marksObtained : ""}
                      onChange={(e) =>
                        handleChangeCell(
                          row,
                          exam.id,
                          "marksObtained",
                          e.target.value,
                        )
                      }
                      sx={{ width: 80, mr: 1 }}
                    />
                    <TextField
                      variant="outlined"
                      size="small"
                      type="number"
                      placeholder="Max"
                      value={cell ? cell.maxMarks : ""}
                      onChange={(e) =>
                        handleChangeCell(
                          row,
                          exam.id,
                          "maxMarks",
                          e.target.value,
                        )
                      }
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PerformanceTable;

